import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import {
  AddEditCustomerUploadMaster,
  GetCustomerUploadMasterList,
  GetSavedDocument,
} from "../../../utils/api.constant";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { saveAs } from "file-saver";
import AuthUtil from "../../../utils/auth";
import { ToastType, ToastOpen } from "../../../state/toast/slice";
import CustomDrawer from "../../../components/drawer";
import { TFileManagerMaster } from "../../../types/index";
import { UserContext } from "../../../context";
import ActionCard from "../../../components/actionCard/actionCard";
import { TContext, TFilterModel, TPrivilege } from "../../../types/config";
import GenericPopover from "../../../components/pop-over";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import { defaultFilterParams } from "../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import FileManagerDetailList from "./detail-list";
import BulkUploadForm from "./bulk-upload-form";
import CustomModal from "../../../components/modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import Utils from "../../../utils";
import { useColumnSearch } from "../../../components/table/column-search";

const FILE_MANAGER_LIST_COLUMN_KEY = "fileManagerList";

export default function FileManagerList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TFileManagerMaster[]>([]);
  const context: TContext = useContext(UserContext);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [selectedFile, setSelectedFile] = useState<TFileManagerMaster | null>(
    null,
  );
  const { getDateRangeFilterProps } = useColumnSearch<TFileManagerMaster>();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const initialColumns: any[] = [];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        FILE_MANAGER_LIST_COLUMN_KEY,
      );
      if (savedPreferences) {
        const initialKeys = new Set(initialColumns.map((c) => c.key));
        const savedKeys = new Set(savedPreferences.map((p: any) => p.key));

        if (
          initialKeys.size !== savedKeys.size ||
          !Array.from(initialKeys).every((key) => savedKeys.has(key))
        ) {
          // Mismatch detected, clear the invalid preferences
          clearColumnPreferences(FILE_MANAGER_LIST_COLUMN_KEY);
          setColumns(initialColumns);
          setDraftColumns(initialColumns);
          return;
        }

        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col]),
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
    setColumns([
      {
        title: "Customer Name",
        dataIndex: "customerName",
        key: "customerName",
        // width: "20%",
        filterSearch: true,
        sorter: (a: TFileManagerMaster, b: TFileManagerMaster) =>
          a.customerName.localeCompare(b.customerName),
        onFilter: (
          value: string | number | boolean,
          record: TFileManagerMaster,
        ) =>
          record.customerName?.toLowerCase() ===
          (value as string).toLowerCase(),
      },
      {
        title: "File Name",
        dataIndex: "uploadFileName",
        key: "uploadFileName",
        // width: "20%",
        filterSearch: true,
        sorter: (a: TFileManagerMaster, b: TFileManagerMaster) =>
          a.uploadFileName.localeCompare(b.uploadFileName),
        onFilter: (
          value: string | number | boolean,
          record: TFileManagerMaster,
        ) =>
          record.uploadFileName?.toLowerCase() ===
          (value as string).toLowerCase(),
        render: (text: string, record: TFileManagerMaster) => {
          const handleDownload = async (
            e: React.MouseEvent<HTMLAnchorElement>,
          ) => {
            e.preventDefault();
            if (!record.documentModel) return;

            try {
              setIsLoading(true);
              const response = await fetch(
                `${
                  process.env.REACT_APP_PUBLIC_API_BASEURL
                }${GetSavedDocument}?id=${record.documentModel.id}`,
                {
                  method: "GET",
                  headers: {
                    ...AuthUtil.getToken(),
                  },
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                saveAs(blob, record.uploadFileName);
              } else {
                onShowToast({
                  type: ToastType.error,
                  title: <CloseCircleFilled />,
                  position: ToastOpen.leftBottom,
                  content: "Failed to download file",
                });
              }
            } catch (error) {
              onShowToast({
                type: ToastType.error,
                title: <CloseCircleFilled />,
                position: ToastOpen.leftBottom,
                content: "An error occurred while downloading the file",
              });
            } finally {
              setIsLoading(false);
            }
          };

          return record.documentModel ? (
            <a href="#" onClick={handleDownload} style={{ cursor: "pointer" }}>
              {text}
            </a>
          ) : (
            text
          );
        },
      },
      {
        title: "Record Count",
        dataIndex: "recordCount",
        key: "recordCount",
        // width: "15%",
        sorter: (a: TFileManagerMaster, b: TFileManagerMaster) =>
          a.recordCount - b.recordCount,
      },
      {
        title: "Uploaded By",
        dataIndex: "createdByName",
        key: "createdByName",
        // width: "15%",
        filterSearch: true,
        sorter: (a: TFileManagerMaster, b: TFileManagerMaster) =>
          a.createdByName.localeCompare(b.createdByName),
        onFilter: (
          value: string | number | boolean,
          record: TFileManagerMaster,
        ) =>
          record.createdByName?.toLowerCase() ===
          (value as string).toLowerCase(),
      },
      {
        title: "Uploaded On",
        dataIndex: "createdOn",
        key: "createdOn",
        // width: "15%",
        ...getDateRangeFilterProps("createdOn"),
        sorter: (a: TFileManagerMaster, b: TFileManagerMaster) =>
          new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
        render: (createdOn: string) => {
          if (createdOn) {
            return DateUtils.format(createdOn, context?.user?.dateFormat, true);
          }
          return "-"; // Or any placeholder for empty values
        },
      },
      {
        title: "Action",
        key: "actions",
        align: "center",
        fixed: "right",
        // width: "6%",
        render: (record: TFileManagerMaster) => (
          <>
            <GenericPopover
              placement="left"
              content={
                <div className="standardActionBox">
                  {/* Edit Row with CustomDrawer trigger */}

                  {Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.VIEWUPLOADEDFILES,
                  ) ? (
                    <CustomDrawer
                      trigger={
                        <ActionCard text="View">
                          <EyeFilled />
                        </ActionCard>
                      }
                      component={FileManagerDetailList}
                      componentProps={{
                        id: record.id,
                      }}
                      placement="bottom"
                      title="Customer Form"
                      // width={1000}
                    />
                  ) : null}
                  {Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETEUPLOADEDFILE,
                  ) ? (
                    <ActionCard
                      text={"Delete"}
                      onClick={() => {
                        setSelectedFile(record);
                        setIsDeleteModalVisible(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </ActionCard>
                  ) : null}
                </div>
              }
              icon={
                <MoreOutlined
                  style={{
                    fontSize: "20px",
                    cursor: "pointer",
                    display:
                      Utils.isUserHasAccess(
                        context.user?.privilegeList as TPrivilege[],
                        ePrivileges.VIEWUPLOADEDFILES,
                      ) ||
                      Utils.isUserHasAccess(
                        context.user?.privilegeList as TPrivilege[],
                        ePrivileges.DELETEUPLOADEDFILE,
                      )
                        ? "visible"
                        : "none",
                  }}
                />
              }
            />
          </>
        ),
      },
    ]);
  }, [context.user]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerUploadMasterList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Transform data to match table columns
        const formattedData = data.map((item: any, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          ...item,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch file data",
      });
      setIsLoading(false);
    }
  };

  const UpdateFile = async (fileDetails: TFileManagerMaster) => {
    try {
      setIsLoading(true);
      const { isDeleted, ...rest } = fileDetails;
      const requestPayload = {
        data: {
          ...rest,
          isDeleted: 1,
        },
      };

      const response = await post(AddEditCustomerUploadMaster, requestPayload);
      const { dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        fetchData();
        setIsLoading(false);
        setSelectedFile(null);
        setIsDeleteModalVisible(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch file data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TFileManagerMaster[]) => {
    const getFilters = (key: keyof TFileManagerMaster) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-",
            ) as string[],
        ),
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };
    const customerNameFilters = getFilters("customerName");
    const uploadFileNameFilters = getFilters("uploadFileName");
    const createdByNameFilters = getFilters("createdByName");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        if (col.key === "uploadFileName") {
          return { ...col, filters: uploadFileNameFilters };
        }
        if (col.key === "createdByName") {
          return { ...col, filters: createdByNameFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(searchText.toLowerCase()),
    );

    const customerNameMatch =
      !activeFilters.customerName ||
      activeFilters.customerName.includes(item.customerName);

    const uploadFileNameMatch =
      !activeFilters.uploadFileName ||
      activeFilters.uploadFileName.includes(item.uploadFileName);

    const createdByNameMatch =
      !activeFilters.createdByName ||
      activeFilters.createdByName.includes(item.createdByName);

    return (
      searchMatch &&
      customerNameMatch &&
      uploadFileNameMatch &&
      createdByNameMatch
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(FILE_MANAGER_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(FILE_MANAGER_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Files`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.UPLOADWARRANTYFILE,
          ) ? (
            <Button
              disabled={isLoading}
              type="primary"
              onClick={() => setShowBulkUploadModal(true)}
            >
              Warranty File Upload
            </Button>
          ) : null}
          {/* <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Warranty File Upload
              </Button>
            }
            component={BulkUploadForm}
            componentProps={{
              id: 0,
              onRefreshList: () => {
                fetchData();
              },
            }}
            title="Customer Form"
            size="default"
          /> */}
          {/* ) : null} */}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={data}
            allColumns={initialColumns}
            filteredData={filteredData}
            visibleColumns={visibleColumns}
            moduleName="File Manager"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm Delete"
        isOpen={isDeleteModalVisible}
        onOk={async () => {
          await UpdateFile(selectedFile as TFileManagerMaster);
        }}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{selectedFile?.uploadFileName}</b>?
        </p>
      </CustomModal>
      <CustomModal
        title="Upload Warranty File"
        style={{ top: "20px" }}
        isOpen={showBulkUploadModal}
        onOk={async () => {}}
        onCancel={() => setShowBulkUploadModal(false)}
        // okText="Yes"
        cancelText="Close"
        cusHeader={true}
        hideOkButton={true}
        width={1280}
      >
        <BulkUploadForm
          onCloseDrawer={() => setShowBulkUploadModal(false)}
          onRefreshList={() => {
            fetchData();
          }}
        ></BulkUploadForm>
      </CustomModal>
    </div>
  );
}
